export interface SearchResult {
  title: string;
  url: string;
  snippet: string;
}

const DUCKDUCKGO_PROXY_METHODS = [
  'instant',
  'lite',
  'html'
] as const;



import { fetch } from '@tauri-apps/plugin-http';

// Helper to determine if we can use Tauri fetch (avoids CORS)
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

// Check if we are running in Tauri or just regular web
const GATEWAY_URL = ''; // Relative path, handled by Vite Proxy or Gateway static serve

// Unified fetch wrapper that prefers Tauri fetch if available
async function smartFetch(url: string, options?: RequestInit): Promise<Response> {
  if (isTauri) {
    try {
      return await fetch(url, options);
    } catch (e) {
      console.warn('[Search] Tauri fetch failed, falling back to browser fetch:', e);
    }
  }
  return window.fetch(url, options);
}

export async function searchSearXNG(query: string, maxResults: number = 5): Promise<SearchResult[]> {
  try {
    // Try to reach the gateway with a short timeout to fail fast
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1500); // 1.5s timeout for local gateway

    // Gateway is typically local, so browser fetch is fine (and faster if same origin),
    // but we use smartFetch for consistency.
    const response = await smartFetch(`${GATEWAY_URL}/v1/search?q=${encodeURIComponent(query)}`, {
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Fallback to DDG if Gateway/SearXNG is down
      console.warn('[Search] SearXNG gateway returned error, falling back to DuckDuckGo');
      return searchDuckDuckGo(query, maxResults);
    }

    const data = await response.json();

    if (!data.results || !Array.isArray(data.results)) {
      return searchDuckDuckGo(query, maxResults);
    }

    return data.results.slice(0, maxResults).map((r: any) => ({
      title: r.title || 'No Title',
      url: r.url,
      snippet: r.content || r.snippet || ''
    }));

  } catch (e) {
    console.warn('[Search] SearXNG/Gateway unavailable (likely not running), falling back to DuckDuckGo', e);
    return searchDuckDuckGo(query, maxResults);
  }
}

export async function searchDuckDuckGo(query: string, maxResults: number = 5): Promise<SearchResult[]> {
  const results: SearchResult[] = [];

  try {
    const instantResults = await fetchDDGInstant(query);
    results.push(...instantResults.slice(0, Math.ceil(maxResults / 2)));
  } catch (e) {
    console.warn('[Search] Instant answer failed:', e);
  }

  if (results.length < maxResults) {
    try {
      const htmlResults = await fetchDDGHtml(query, maxResults - results.length);
      results.push(...htmlResults);
    } catch (e) {
      console.warn('[Search] HTML search failed:', e);
    }
  }

  return results.slice(0, maxResults);
}

async function fetchDDGInstant(query: string): Promise<SearchResult[]> {
  const url = `https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json&no_html=1&skip_disambig=1`;

  // Use smartFetch to avoid CORS if in Tauri
  const response = await smartFetch(url);
  if (!response.ok) throw new Error(`DDG API error: ${response.status}`);

  const data = await response.json();
  const results: SearchResult[] = [];

  if (data.AbstractText && data.AbstractURL) {
    results.push({
      title: data.Heading || 'Summary',
      url: data.AbstractURL,
      snippet: data.AbstractText
    });
  }

  if (data.RelatedTopics) {
    for (const topic of data.RelatedTopics.slice(0, 4)) {
      if (topic.Text && topic.FirstURL) {
        results.push({
          title: topic.Text.split(' - ')[0] || 'Related',
          url: topic.FirstURL,
          snippet: topic.Text
        });
      }
    }
  }

  return results;
}

async function fetchDDGHtml(query: string, maxResults: number): Promise<SearchResult[]> {
  const url = `https://html.duckduckgo.com/html/?q=${encodeURIComponent(query)}`;

  try {
    // DDG HTML often blocks non-browser UAs, but Tauri fetch can mimic better or bypass CORS
    const response = await smartFetch(url, {
      headers: {
        'Accept': 'text/html',
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
      }
    });

    if (!response.ok) throw new Error(`DDG HTML error: ${response.status}`);

    const html = await response.text();
    return parseSearchResults(html, maxResults);
  } catch (e) {
    console.warn('[Search] DDG HTML fetch failed, returning empty results');
    return [];
  }
}

function parseSearchResults(html: string, maxResults: number): SearchResult[] {
  const results: SearchResult[] = [];

  const resultPattern = /<a[^>]+class="result__a"[^>]*href="([^"]+)"[^>]*>([^<]+)<\/a>[\s\S]*?<a[^>]+class="result__snippet"[^>]*>([^<]+)/gi;

  let match;
  while ((match = resultPattern.exec(html)) !== null && results.length < maxResults) {
    const [, url, title, snippet] = match;
    if (url && title) {
      results.push({
        title: decodeHtmlEntities(title.trim()),
        url: url.startsWith('//') ? `https:${url}` : url,
        snippet: decodeHtmlEntities(snippet?.trim() || '')
      });
    }
  }

  if (results.length === 0) {
    const simplePattern = /<a[^>]+href="(https?:\/\/[^"]+)"[^>]*>([^<]{10,100})<\/a>/gi;
    while ((match = simplePattern.exec(html)) !== null && results.length < maxResults) {
      const [, url, title] = match;
      if (url && title && !url.includes('duckduckgo.com')) {
        results.push({
          title: decodeHtmlEntities(title.trim()),
          url,
          snippet: ''
        });
      }
    }
  }

  return results;
}

function decodeHtmlEntities(text: string): string {
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&nbsp;': ' '
  };

  return text.replace(/&[^;]+;/g, match => entities[match] || match);
}

export function formatSearchResultsForContext(results: SearchResult[]): string {
  if (results.length === 0) return '';

  let context = '\n\n=== WEB SEARCH RESULTS ===\n';

  for (const result of results) {
    context += `\n**${result.title}**\n`;
    context += `Source: ${result.url}\n`;
    if (result.snippet) {
      context += `${result.snippet}\n`;
    }
    context += '\n';
  }

  context += '=== END SEARCH RESULTS ===\n';

  return context;
}

export async function performSearch(query: string): Promise<{ context: string; sources: SearchResult[] }> {
  // Use SearXNG with DDG fallback
  const results = await searchSearXNG(query, 5);
  return {
    context: formatSearchResultsForContext(results),
    sources: results
  };
}
